import Vue from 'vue';
import Modal from 'components/widgets/Modal.vue';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
import Project from 'src/api/project';
import { FIELD_TYPES, FIELD_TYPES_INDEX } from "components/project/create/SchemaUtils";
import FormatUtils from 'src/utils/formatters';
const AMBIG_DATE_RE = /^\d{1,2}[\/.-]\d{1,2}[\/.-]\d{2,4}/;
export default Vue.extend({
    components: { Modal, Dropdown, DropdownItem },
    props: {
        samples: { type: Array, default: null },
        schema: { type: Array, default: () => [] },
        numRecords: { type: Number, default: null },
        /* Allows disabling type selections for specific fields */
        extraTypeOptions: { type: Map, default: () => new Map() },
        columnLimit: { type: Number, required: true },
        rowLimit: { type: Number, required: true },
        collapseIgnored: { type: Boolean, default: false },
        enforceRowLimit: { type: Boolean, default: false },
    },
    data() {
        return {
            fieldTypes: FIELD_TYPES,
            fieldTypesIndex: FIELD_TYPES_INDEX,
            ambiguousDateExample: null,
            dateSelectionCallbackFn: null,
            noTextFields: false,
            selectedDateType: null,
            tooManyNps: false,
            tooManyColumns: false,
            tooManyRows: false,
            underscoreNames: false,
        };
    },
    computed: {
        numUsedColumns() {
            // return valid ID type column count from schema
            return this.schema?.filter((schema) => schema.type >= 0 && !schema.unique_group).length ?? 0;
        },
        fieldOptions() {
            let opts = {};
            this.schema.forEach((col, index) => {
                const extraOptions = this.extraTypeOptions.get(col.name);
                let types = FIELD_TYPES;
                if (extraOptions !== undefined) {
                    types = FIELD_TYPES.map(ft => {
                        return {
                            ...ft,
                            ...extraOptions.get(ft.value) || {},
                        };
                    });
                }
                // never ignore a column named 'Response ID' for qualtrics.
                // As we make this a uniqueID, so we never ignore it.
                if (col.name.toUpperCase() === 'RESPONSE ID') {
                    types = types.filter(t => t.label === "Category");
                    this.updateFieldType(index, 5);
                }
                // always ignore a column named 'NPS Category' to avoid
                // clashes with our own calculated fields
                if (col.name.toUpperCase() === 'NPS CATEGORY') {
                    types = types.filter(t => t.label === "Ignore");
                    this.updateFieldType(index, -1);
                }
                opts[col.name] = types;
            });
            return opts;
        }
    },
    methods: {
        number: FormatUtils.number,
        // This method checks for ambiguous dates and prompts the user for input if necessary.
        // Ambiguous dates are of the form xx/xx/yy
        // In this case we need to know if day or month come first.
        checkForAmbiguousDates(callbackFn) {
            if (!this.selectedDateType) {
                this.ambiguousDateExample = null;
                let dateFields = Object.values(this.schema).filter(entry => Project.COLUMN_INDEXED_TYPES.get(entry.type).startsWith('DATE'));
                if (dateFields.length > 0) {
                    for (let sample of this.samples) {
                        for (let f of dateFields) {
                            // Note: Rows in the input file might be missing empty trailing columns;
                            // this is why we check that the field index is less than the sample length.
                            if (f.index < sample.length && sample[f.index].match(AMBIG_DATE_RE)) {
                                this.ambiguousDateExample = sample[f.index];
                                this.dateSelectionCallbackFn = callbackFn;
                                this.$refs.selectDateTypeModal.show();
                                return;
                            }
                        }
                    }
                }
            }
            callbackFn();
        },
        isValid() {
            this.tooManyColumns = false;
            this.tooManyRows = false;
            this.noTextFields = false;
            this.tooManyNps = false;
            this.underscoreNames = false;
            if (this.numUsedColumns > this.columnLimit) {
                this.tooManyColumns = true;
            }
            if (this.numRecords > this.rowLimit && this.enforceRowLimit) {
                this.tooManyRows = true;
            }
            if (Object.values(this.schema).filter(entry => entry.type === 1).length <= 0) {
                // Check if there are any text fields mapped
                this.noTextFields = true;
            }
            if (Object.values(this.schema).filter(entry => entry.type === 7).length > 1) {
                // Check if there is more than one NPS field
                this.tooManyNps = true;
            }
            for (let f of Object.values(this.schema).filter(entry => entry.type !== -1)) {
                // Check if there are mapped columns that start with an underscore
                if (f.name.startsWith('_')) {
                    this.underscoreNames = true;
                    break;
                }
            }
            return !(this.noTextFields || this.tooManyNps || this.underscoreNames || this.tooManyColumns || this.tooManyRows);
        },
        selectDateType(type) {
            this.selectedDateType = type;
            this.dateSelectionCallbackFn();
        },
        updateFieldType(index, newType) {
            this.$emit('type-changed', index, newType);
        },
        handleFieldClick($event, field) {
            if (field.type !== -1)
                return;
            if (!this.collapseIgnored)
                return;
            $event.currentTarget.classList.toggle('collapse');
            for (const child of $event.currentTarget.children) {
                child.classList.toggle('hidden');
            }
        }
    }
});
